import { createSlice } from '@reduxjs/toolkit';

import { Pagination } from 'common/components/Table/CommonTableStyles';
import { DATAINGESTIONTABS } from 'common/constants/DataIngestionConstants';
import { ReportingLibraryTabs } from 'common/constants/ReportingLibraryConstants';
import { TAXONOMY_ENTITY_INDEX } from 'common/constants/TaxonomyConstants';

import {
	IDataIngestionState,
	IDocumentInfoElements,
} from 'features/DataIngestion/api/type-def';
import { IActionReviewPayload } from 'features/HomePage/api/type-def';
import { ITaskTabContent } from 'features/HorizonScanning/api/type-def';
import {
	IGPTMessageProps,
	IKnowledgeBaseOptions,
} from 'features/OpenRiskGPT/api/type-def';
import {
	IFileInfo,
	IReportingLibraryState,
	IUploadingFile,
	IUploadingFileStatus,
} from 'features/Reporting/api/type-def';
import { IRenegerateChanges } from 'features/RiskProfile/Taxonomy/api/type-def';

import { IRowsPerPaginationObj, IToastMessage } from '../../api/type-def';
import {
	PAGINATION_CONSTANTS,
	TABLE_CONSTANTS,
	ViewType,
} from '../../constants';
import storageWrapper from '../../loginUtils/storage-wrapper';

import { IUser } from '../../../features/Settings/UserManagement/api/type-def';

import type { PayloadAction } from '@reduxjs/toolkit';
import type { IHighlight } from 'react-pdf-highlighter';

const { customPageSize } = TABLE_CONSTANTS;

const { RECENTLY_VIEWED_TAB } = ReportingLibraryTabs;
const { ALL } = DATAINGESTIONTABS;

interface IControlType {
	id: string;
	type: string;
	title: string;
	reviewStatus?: string;
	generateMode?: string;
	aiScore?: string;
	parentId?: string;
	impactObjectId?: string;
	createdBy?: string;
	displayId?: string;
	childDisplayId?: string;
	mappedUserId?: string;
	uniqueId?: string;
	citationGroupBusinessUnitIds?: string[];
	swap?: boolean;
	citationGroupId?: string;
}

interface IPreviousChatSessionDetails {
	messages: IGPTMessageProps[];
	sessionId: string;
	chatName: string;
}

interface IObligationComments {
	id: string;
	email: string;
	comment: string;
	fullName?: string;
	createdDate?: string;
	simulationDetailId?: string;
	paragraphId?: string;
}

export interface IUploadedDocumentProgress {
	index: number;
	progress: number;
}

export interface IMyApprovalSelectedItem {
	changeCount: number;
	displayId: string;
	id: string;
	name: string;
	reviewStatus: string;
	type: string;
}
export interface ICommonState {
	language: string;
	isAppDrawerOpen: boolean;
	isUserListChecked: boolean;
	fileDrawer: boolean;
	mappingDrawer: boolean;
	processingFileName: string;
	processingFileDataType: string;
	processingFilePath: string;
	fileUploadStage: number;
	loggedInUser: IUser | null;
	selectedEntityTypeData: IControlType;
	pagination: Pagination;
	rowsPerPage: IRowsPerPaginationObj;
	riskGptDrawer: boolean;
	riskGptShouldExpand: boolean;
	riskGptAllowDrag: boolean;
	riskGptKnowledgeBase: string;
	riskGptKnowledgeBaseOptions: IKnowledgeBaseOptions[];
	isError: boolean;
	errorMessage: string;
	isAuthenticated: boolean;
	returnTo: string | null;
	regInvententoryCreateDrawer: boolean;
	regInvententoryAddToGroupDrawer: boolean;
	solutionSideDrawer: boolean;
	simulationCommonSaveDrawer: boolean;
	simulationCommonShareDrawer: boolean;
	selectedTabName: string;
	selectedTabIndex: number;
	regChangeSearchText: string;
	regChangeSelectedTabIndex: number;
	homePageSelectedTabIndex: number;
	myApprovalsSelectedTabIndex: number;
	myApprovalsData: IActionReviewPayload[];
	requirementInventoryBreadcrumbText: string;
	tablePaginationHolder: {
		[key: string]: Pagination;
	};
	tableQueryFlag: boolean; // use to indicate if we use the stored pagination info or initial one
	multiSelectProductFilterData: string[];
	multiSelectInstitutionFilterData: string[];
	multiSelectJurisdictionFilterData: string[];
	horizonScanningSearchText: string;
	tasksListSortData: any;
	landingPageSearchText: string;
	landingPageTableData: any;
	taskListViewType: ViewType;
	riskGptSessionDetails: IPreviousChatSessionDetails;
	obligationToPolicyCommentList: IObligationComments[];
	obligtionToPolicyRegenerate: boolean;
	landingPageFilterData: any;
	landingPageCategoriesData: any;
	taskRenderStatus: boolean;
	taskTableMappedData: ITaskTabContent[];
	taskRowData: any;
	appThemeDrawerState: boolean;
	isRelationshipMappingDeleted: boolean;
	isLDResponseAvailable: boolean;
	selectedReportingLibraryTab: IReportingLibraryState;
	selectedDataIngestionTab: IDataIngestionState;
	reUploadHistory: IUploadingFileStatus | null;
	uploadedHistory: IFileInfo[] | null;
	activeChatSessionUploadingFiles: IUploadingFile[];
	activeChatSessionUploadedFiles: any[];
	activeChatSearchTextSelected: boolean;
	uploadedDocumentProgress: IUploadedDocumentProgress;
	toastMessage: IToastMessage;
	isPRCDetailsPage: boolean;
	deleteAIGeneratedCardPayload: IRenegerateChanges;
	selectedMyApprovalItem: IMyApprovalSelectedItem;
	isApprovalDrawerOpen: boolean;
	isApprovalDrawerTabIndex: number;
	fromHistory: boolean;
	approvalDrawerTitle: string;
	isControlQualityRetry: boolean;
	isBulkUploadSuccess: boolean;
	isBulkUploadFailed: boolean;
	promptSearchText: string;
	uploadParserSelectedTab: number;
	parserHighlights: Array<IHighlight>;
	parserDocumentInfo: IDocumentInfoElements;
	highlightButtonText: string;
	parserEndHighlightId: IHighlight;
}

const initialState: ICommonState = {
	language: 'en',
	isAppDrawerOpen: true,
	isUserListChecked: false,
	fileDrawer: false,
	processingFileName: '',
	processingFileDataType: '',
	processingFilePath: '',
	mappingDrawer: false,
	selectedEntityTypeData: { id: '', title: '', type: '' },
	fileUploadStage: 0,
	loggedInUser: storageWrapper.getStorageItem('user') || { userId: '' },
	pagination: <Pagination>{
		sortKey: '',
		pageSize: PAGINATION_CONSTANTS.PAGE_SIZE,
		pageNumber: PAGINATION_CONSTANTS.PAGE_NUMBER,
		sortDirection: '',
	},
	rowsPerPage: <IRowsPerPaginationObj>{
		rowsPerPage: customPageSize,
	},
	riskGptDrawer: false,
	riskGptAllowDrag: true,
	isError: false,
	errorMessage: '',
	isAuthenticated: storageWrapper.getStorageItem('loginState') || false,
	returnTo: storageWrapper.getStorageItem('returnTo'),
	regInvententoryCreateDrawer: false,
	regInvententoryAddToGroupDrawer: false,
	solutionSideDrawer: false,
	simulationCommonSaveDrawer: false,
	simulationCommonShareDrawer: false,
	selectedTabName: `${TAXONOMY_ENTITY_INDEX.PROCESS}-${process.env.REACT_APP_ENVIRONMENT}`,
	selectedTabIndex: 0, // Assuming a default value
	myApprovalsSelectedTabIndex: 0,
	myApprovalsData: [],
	regChangeSearchText: '',
	tablePaginationHolder: {},
	regChangeSelectedTabIndex: 0,
	homePageSelectedTabIndex: 0,
	requirementInventoryBreadcrumbText: 'Requirements Inventory',
	multiSelectProductFilterData: [],
	multiSelectInstitutionFilterData: [],
	multiSelectJurisdictionFilterData: [],
	tableQueryFlag: false,
	horizonScanningSearchText: '',
	tasksListSortData: {},
	landingPageSearchText: '',
	landingPageTableData: {},
	landingPageFilterData: '',
	taskListViewType: ViewType.GRID,
	riskGptShouldExpand: false,
	riskGptKnowledgeBase: '',
	riskGptKnowledgeBaseOptions: [],
	riskGptSessionDetails: {
		messages: [],
		sessionId: '',
		chatName: '',
	},
	obligationToPolicyCommentList: [],
	obligtionToPolicyRegenerate: false,
	landingPageCategoriesData: {},
	taskRenderStatus: true,
	taskTableMappedData: [],
	taskRowData: {},
	appThemeDrawerState: false,
	isRelationshipMappingDeleted: false,
	isLDResponseAvailable: false,
	selectedReportingLibraryTab: {
		reportingLibraryTabIndex: 0,
		reportingLibraryTabName: RECENTLY_VIEWED_TAB,
	},
	selectedDataIngestionTab: {
		dataIngestionTabIndex: 0,
		dataIngestionTabName: ALL,
	},
	reUploadHistory: null,
	uploadedHistory: null,
	activeChatSessionUploadingFiles: [],
	activeChatSessionUploadedFiles: [],
	activeChatSearchTextSelected: false,
	uploadedDocumentProgress: { index: 0, progress: 0 },
	toastMessage: {
		severity: '',
		message: '',
		isToastOpen: false,
	},
	isPRCDetailsPage: false,
	deleteAIGeneratedCardPayload: {
		parentObjectId: '',
		parentObjectType: '',
		text: '',
		source: '',
		industryType: '',
	},
	selectedMyApprovalItem: {
		changeCount: 0,
		displayId: '',
		id: '',
		name: '',
		reviewStatus: '',
		type: '',
	},
	isApprovalDrawerOpen: false,
	isApprovalDrawerTabIndex: 0,
	fromHistory: false,
	approvalDrawerTitle: '',
	isControlQualityRetry: false,
	isBulkUploadSuccess: false,
	isBulkUploadFailed: false,
	promptSearchText: '',
	uploadParserSelectedTab: 0,
	parserHighlights: [],
	parserDocumentInfo: {
		bookTitle: '',
		issuer: '',
		issuanceType: '',
		jurisdiction: '',
		issuanceDate: '',
		publishedDate: '',
	},
	highlightButtonText: 'Highlight',
	parserEndHighlightId: {
		id: '',
		position: {
			boundingRect: { x1: 0, x2: 0, y1: 0, y2: 0, width: 0, height: 0 },
			pageNumber: 1,
			rects: [],
		},
		comment: { text: '', emoji: '' },
		content: {},
	},
};

export const commonSlice = createSlice({
	name: 'common',
	initialState,
	reducers: {
		setLanguage: (state, action: PayloadAction<string>) => {
			state.language = action.payload;
		},
		setAppDrawer: (state, action: PayloadAction<boolean>) => {
			state.isAppDrawerOpen = action.payload;
		},
		setUserListChecked: (state, action: PayloadAction<boolean>) => {
			state.isUserListChecked = action.payload;
		},
		setLoggedInUser: (state, action: PayloadAction<IUser | null>) => {
			state.loggedInUser = action.payload;
		},
		setPagination: (state, action: PayloadAction<Pagination>) => {
			state.pagination = action?.payload;
		},
		setFileDrawer: (state, action: PayloadAction<boolean>) => {
			state.fileDrawer = action?.payload;
		},
		setProcessingFileName: (state, action: PayloadAction<string>) => {
			state.processingFileName = action.payload;
		},
		setProcessingFileDataType: (state, action: PayloadAction<string>) => {
			state.processingFileDataType = action.payload;
		},
		setProcessingFilePath: (state, action: PayloadAction<string>) => {
			state.processingFilePath = action.payload;
		},
		setMappingDrawer: (state, action: PayloadAction<boolean>) => {
			state.mappingDrawer = action?.payload;
		},
		setSelectedEntityTypeData: (state, action: PayloadAction<any>) => {
			state.selectedEntityTypeData = action?.payload;
		},
		setFileUploadStage: (state, action: PayloadAction<number>) => {
			state.fileUploadStage = action?.payload;
		},
		setRowsPerPagination: (
			state,
			action: PayloadAction<IRowsPerPaginationObj>,
		) => {
			state.rowsPerPage = action?.payload;
		},
		setRiskGptDrawer: (state, action: PayloadAction<boolean>) => {
			state.riskGptDrawer = action.payload;
		},
		setIsError: (state, action: PayloadAction<boolean>) => {
			state.isError = action.payload;
		},
		setErrorMessage: (state, action: PayloadAction<string>) => {
			state.errorMessage = action.payload;
		},
		setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
			state.isAuthenticated = action.payload;
		},
		setReturnTo: (state, action: PayloadAction<string | null>) => {
			state.returnTo = action.payload;
		},
		setRegInvententoryCreateDrawer: (state, action: PayloadAction<boolean>) => {
			state.regInvententoryCreateDrawer = action.payload;
		},
		setRegInvententoryAddToGroupDrawer: (
			state,
			action: PayloadAction<boolean>,
		) => {
			state.regInvententoryAddToGroupDrawer = action.payload;
		},
		setSimulationCommonSaveDrawer: (state, action: PayloadAction<boolean>) => {
			state.simulationCommonSaveDrawer = action.payload;
		},
		setSimulationCommonShareDrawer: (state, action: PayloadAction<boolean>) => {
			state.simulationCommonShareDrawer = action.payload;
		},
		setSolutionSideDrawer: (state, action: PayloadAction<boolean>) => {
			state.solutionSideDrawer = action.payload;
		},

		setRiskRefSelectedTab: (
			state,
			action: PayloadAction<{ tableIndex: string; indexType: number }>,
		) => {
			state.selectedTabName = action.payload.tableIndex;
			state.selectedTabIndex = action.payload.indexType;
		},
		setRegChangeSearchText: (state, action: PayloadAction<string>) => {
			state.regChangeSearchText = action.payload;
		},
		setRegChangeSelectedTabIndex: (state, action: PayloadAction<number>) => {
			state.regChangeSelectedTabIndex = action.payload;
		},
		setMyApprovalsSelectedTabIndex: (state, action: PayloadAction<number>) => {
			state.myApprovalsSelectedTabIndex = action.payload;
		},
		setHomePageTabIndex: (state, action: PayloadAction<number>) => {
			state.homePageSelectedTabIndex = action.payload;
		},
		setRequirementInventoryBreadcrumbText: (
			state,
			action: PayloadAction<string>,
		) => {
			state.requirementInventoryBreadcrumbText = action.payload;
		},
		setTablePaginationHolder: (
			state,
			action: PayloadAction<{ [key: string]: Pagination }>,
		) => {
			state.tablePaginationHolder = {
				...state.tablePaginationHolder,
				...action.payload,
			};
		},
		clearTablePaginationHolder: (state) => {
			state.tablePaginationHolder = {};
		},
		setMultiSelectProductFilterData: (
			state,
			action: PayloadAction<string[]>,
		) => {
			state.multiSelectProductFilterData = action.payload;
		},
		setMultiSelectInstitutionFilterData: (
			state,
			action: PayloadAction<string[]>,
		) => {
			state.multiSelectInstitutionFilterData = action.payload;
		},
		setMultiSelectJurisdictionFilterData: (
			state,
			action: PayloadAction<string[]>,
		) => {
			state.multiSelectJurisdictionFilterData = action.payload;
		},
		setQueryFlag(state, action: PayloadAction<boolean>) {
			state.tableQueryFlag = action.payload;
		},
		setHorizonScanningSearchText(state, action: PayloadAction<string>) {
			state.horizonScanningSearchText = action.payload;
		},
		setLandingPageSearchText(state, action: PayloadAction<string>) {
			state.landingPageSearchText = action.payload;
		},
		setLandingPageTableData(state, action: PayloadAction<any>) {
			state.landingPageTableData = action.payload;
		},
		setLandingPageTableClearData(state) {
			state.landingPageTableData.records = [];
		},
		setLandingPageFilterData(state, action: PayloadAction<any>) {
			state.landingPageFilterData = action.payload;
		},
		setTaskListViewType: (state, action: PayloadAction<ViewType>) => {
			state.taskListViewType = action.payload;
		},
		setRiskGptAllowDrag(state, action: PayloadAction<boolean>) {
			state.riskGptAllowDrag = action.payload;
		},
		setRiskGptShouldExpand(state, action: PayloadAction<boolean>) {
			state.riskGptShouldExpand = action.payload;
		},
		setRiskGptKnowledgeBase(state, action: PayloadAction<string>) {
			state.riskGptKnowledgeBase = action.payload;
		},
		setRiskGptKnowledgeBaseOptions(
			state,
			action: PayloadAction<IKnowledgeBaseOptions[]>,
		) {
			state.riskGptKnowledgeBaseOptions = action.payload;
		},
		setRiskGptSessionDetails(
			state,
			action: PayloadAction<IPreviousChatSessionDetails>,
		) {
			state.riskGptSessionDetails = action.payload;
		},
		setObligationToPolicyCommentList: (
			state,
			action: PayloadAction<IObligationComments[]>,
		) => {
			state.obligationToPolicyCommentList = action.payload;
		},
		setObligtionToPolicyRegenerate: (state, action: PayloadAction<boolean>) => {
			state.obligtionToPolicyRegenerate = action.payload;
		},
		setLandingPageCategoriesData: (state, action: PayloadAction<any>) => {
			state.landingPageCategoriesData = action.payload;
		},
		setTaskRenderStatus: (state, action: PayloadAction<boolean>) => {
			state.taskRenderStatus = action.payload;
		},
		setTaskTableMappedData: (
			state,
			action: PayloadAction<ITaskTabContent[]>,
		) => {
			state.taskTableMappedData = action.payload;
		},
		setTaskRowData: (state, action: PayloadAction<any>) => {
			state.taskRowData = action.payload;
		},
		setAppThemeDrawerState: (state, action: PayloadAction<boolean>) => {
			state.appThemeDrawerState = action.payload;
		},
		setIsRelationshipMappingDeleted: (
			state,
			action: PayloadAction<boolean>,
		) => {
			state.isRelationshipMappingDeleted = action?.payload;
		},
		setIsLDResponseAvailable: (state, action: PayloadAction<boolean>) => {
			state.isLDResponseAvailable = action.payload;
		},
		setReUploadHistory: (
			state,
			action: PayloadAction<IUploadingFileStatus | null>,
		) => {
			state.reUploadHistory = action.payload;
		},
		setUploadedHistory: (state, action: PayloadAction<IFileInfo[] | null>) => {
			state.uploadedHistory = action.payload;
		},
		setActiveChatSessionUploadingFiles: (
			state,
			action: PayloadAction<IUploadingFile[]>,
		) => {
			state.activeChatSessionUploadingFiles = action.payload;
		},
		setActiveChatSessionUploadedFiles: (state, action: PayloadAction<any>) => {
			state.activeChatSessionUploadedFiles = action.payload;
		},
		setSelectedReportingTab: (
			state,
			action: PayloadAction<IReportingLibraryState>,
		) => {
			state.selectedReportingLibraryTab.reportingLibraryTabIndex =
				action.payload.reportingLibraryTabIndex;
			state.selectedReportingLibraryTab.reportingLibraryTabName =
				action.payload.reportingLibraryTabName;
		},
		setSelectedDataIngestionTab: (
			state,
			action: PayloadAction<IDataIngestionState>,
		) => {
			state.selectedDataIngestionTab.dataIngestionTabIndex =
				action.payload.dataIngestionTabIndex;
			state.selectedDataIngestionTab.dataIngestionTabName =
				action.payload.dataIngestionTabName;
		},
		setActiveChatSearchTextSelected: (
			state,
			action: PayloadAction<boolean>,
		) => {
			state.activeChatSearchTextSelected = action.payload;
		},
		setUploadedDocumentProgress: (
			state,
			action: PayloadAction<IUploadedDocumentProgress>,
		) => {
			state.uploadedDocumentProgress = action.payload;
		},
		clearUploadedDocumentProgress: (state) => {
			state.uploadedDocumentProgress.index = 0;
			state.uploadedDocumentProgress.progress = 0;
		},
		setToastMessage: (state, action: PayloadAction<IToastMessage>) => {
			state.toastMessage = action.payload;
		},
		setIsPRCDetailsPage: (state, action: PayloadAction<boolean>) => {
			state.isPRCDetailsPage = action.payload;
		},
		setMyApprovalData: (
			state,
			action: PayloadAction<IActionReviewPayload[]>,
		) => {
			state.myApprovalsData = action.payload;
		},
		setDeleteAIGeneratedCardPayload: (
			state,
			action: PayloadAction<IRenegerateChanges>,
		) => {
			state.deleteAIGeneratedCardPayload = action.payload;
		},
		setSelectedMyApprovalItem: (
			state,
			action: PayloadAction<IMyApprovalSelectedItem>,
		) => {
			state.selectedMyApprovalItem = action.payload;
		},
		setApprovalDrawerState: (state, action: PayloadAction<boolean>) => {
			state.isApprovalDrawerOpen = action.payload;
		},
		setApprovalDrawerTabState: (state, action: PayloadAction<number>) => {
			state.isApprovalDrawerTabIndex = action.payload;
		},
		setFromHistory: (state, action: PayloadAction<boolean>) => {
			state.fromHistory = action.payload;
		},
		setApprovalDrawerTitle: (state, action: PayloadAction<string>) => {
			state.approvalDrawerTitle = action.payload;
		},
		setControlQualityRetry: (state, action: PayloadAction<boolean>) => {
			state.isControlQualityRetry = action.payload;
		},
		setBulkUploadSuccessStatus: (state, action: PayloadAction<boolean>) => {
			state.isBulkUploadSuccess = action.payload;
		},
		setBulkUploadFailedStatus: (state, action: PayloadAction<boolean>) => {
			state.isBulkUploadFailed = action.payload;
		},
		setPromptSearchText: (state, action: PayloadAction<string>) => {
			state.promptSearchText = action.payload;
		},
		setUploadParserSelectedTab: (state, action: PayloadAction<number>) => {
			state.uploadParserSelectedTab = action.payload;
		},
		setParserHighlights: (state, action: PayloadAction<any[]>) => {
			state.parserHighlights = action.payload;
		},
		setParserDocumentInfo: (
			state,
			action: PayloadAction<IDocumentInfoElements>,
		) => {
			state.parserDocumentInfo = action.payload;
		},
		setHighlightButtonText: (state, action: PayloadAction<string>) => {
			state.highlightButtonText = action.payload;
		},
		setParserEndHighlightId: (state, action: PayloadAction<IHighlight>) => {
			state.parserEndHighlightId = action.payload;
		},
	},
});

export const {
	setLanguage,
	setAppDrawer,
	setUserListChecked,
	setProcessingFileName,
	setProcessingFileDataType,
	setProcessingFilePath,
	setLoggedInUser,
	setPagination,
	setFileDrawer,
	setMappingDrawer,
	setSelectedEntityTypeData,
	setFileUploadStage,
	setRowsPerPagination,
	setRiskGptDrawer,
	setIsError,
	setErrorMessage,
	setIsAuthenticated,
	setReturnTo,
	setRegInvententoryCreateDrawer,
	setRegInvententoryAddToGroupDrawer,
	setSimulationCommonSaveDrawer,
	setSimulationCommonShareDrawer,
	setSolutionSideDrawer,
	setRiskRefSelectedTab,
	setRegChangeSearchText,
	setRequirementInventoryBreadcrumbText,
	setRegChangeSelectedTabIndex,
	setHomePageTabIndex,
	setMultiSelectProductFilterData,
	setMultiSelectInstitutionFilterData,
	setMultiSelectJurisdictionFilterData,
	setTablePaginationHolder,
	clearTablePaginationHolder,
	setQueryFlag,
	setHorizonScanningSearchText,
	setLandingPageSearchText,
	setLandingPageTableData,
	setTaskListViewType,
	setRiskGptAllowDrag,
	setRiskGptShouldExpand,
	setRiskGptKnowledgeBase,
	setRiskGptSessionDetails,
	setObligationToPolicyCommentList,
	setObligtionToPolicyRegenerate,
	setLandingPageFilterData,
	setLandingPageCategoriesData,
	setTaskRenderStatus,
	setLandingPageTableClearData,
	setTaskTableMappedData,
	setRiskGptKnowledgeBaseOptions,
	setTaskRowData,
	setAppThemeDrawerState,
	setIsRelationshipMappingDeleted,
	setIsLDResponseAvailable,
	setSelectedReportingTab,
	setSelectedDataIngestionTab,
	setReUploadHistory,
	setUploadedHistory,
	setActiveChatSessionUploadingFiles,
	setActiveChatSessionUploadedFiles,
	setActiveChatSearchTextSelected,
	setUploadedDocumentProgress,
	clearUploadedDocumentProgress,
	setMyApprovalsSelectedTabIndex,
	setToastMessage,
	setIsPRCDetailsPage,
	setDeleteAIGeneratedCardPayload,
	setMyApprovalData,
	setSelectedMyApprovalItem,
	setApprovalDrawerState,
	setApprovalDrawerTabState,
	setFromHistory,
	setApprovalDrawerTitle,
	setControlQualityRetry,
	setBulkUploadSuccessStatus,
	setBulkUploadFailedStatus,
	setPromptSearchText,
	setUploadParserSelectedTab,
	setParserHighlights,
	setParserDocumentInfo,
	setHighlightButtonText,
	setParserEndHighlightId,
} = commonSlice.actions;

export default commonSlice.reducer;
